import EmptyList from '@/components/EmptyList';
import Filters, { orderByFilterEnum } from '@/components/Filter';
import Loader from '@/components/Loader';
import Tab from '@/components/Tab';
import Constants from '@/Constants';
import { useGetPaginated } from '@/hooks/useGetPaginated';
import Item, { ItemRequest } from '@/models/Item';
import { ApplicationState } from '@/store';
import React, { ChangeEvent, FC, useCallback, useEffect, useState } from 'react';
import { useBottomScrollListener } from 'react-bottom-scroll-listener';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import _ from 'underscore';
import ItemCard from './ItemCard';
import './style.scss';

const getQueryParams = ({
    applicationId,
    orgId,
    name,
    mediaType,
    minWorkload,
    maxWorkload,
    visibility,
    status,
    orderBy,
    locale,
    moduleId,
    offset,
    limit,
}: ItemRequest) => {
    let queryParams = `?applicationId=${applicationId}&organizationId=${orgId}`;

    if (name) {
        queryParams += `&name=${name}`;
    }
    if (mediaType) {
        queryParams += `&mediaType=${mediaType}`;
    }
    if (minWorkload) {
        queryParams += `&minWorkload=${minWorkload}`;
    }
    if (maxWorkload) {
        queryParams += `&maxWorkload=${maxWorkload}`;
    }
    if (visibility) {
        queryParams += `&visibility=${visibility}`;
    }
    if (status) {
        queryParams += `&status=${status}`;
    }
    if (orderBy) {
        queryParams += `&orderBy=${orderBy}`;
    }
    if (locale) {
        queryParams += `&locale=${locale}`;
    }
    if (moduleId) {
        queryParams += `&moduleId=${moduleId}`;
    }
    if (_.isNumber(offset)) {
        queryParams += `&offset=${offset}`;
    }
    if (_.isNumber(limit)) {
        queryParams += `&limit=${limit}`;
    }

    return queryParams;
};

const Items: FC = () => {
    const { data: strings } = useSelector((state: ApplicationState) => state.locale);

    const [searchName, setSearchName] = useState('');
    const [active, setActive] = useState(Constants.lessonStatus.PUBLISHED);
    const [initialLoading, setInitialLoading] = useState(true);
    const [initialData, setInitialData] = useState(false);
    // filters
    const [slideRange, setSlideRange] = useState<number[]>([0, 90]);
    const [fakeSlideRange, setFakeSlideRange] = useState<number[]>([0, 90]);
    const [orderBy, setOrderBy] = useState(orderByFilterEnum.CREATED_AT);
    const [mediaType, setMediaType] = useState({ VIDEO: false, AUDIO: false, HTML: false, EXTERNAL: false });
    const [language, setLanguage] = useState({ pt: false, en: false, '': false });
    const [visibility, setVisibility] = useState({ public: false, private: false });
    const [showBottomLoading, setShowBottomLoading] = useState<boolean>(false);

    const history = useHistory();

    const { data: identity } = useSelector((state: ApplicationState) => state.identity);
    const { refreshItem } = useSelector((state: ApplicationState) => state.itens);

    const getKey = (index: number) => {
        const {
            user: { orgId, applicationId },
        } = identity;

        let visibilityParam = undefined;

        if (visibility.private && !visibility.public) visibilityParam = '0';
        if (!visibility.private && visibility.public) visibilityParam = '1';

        let media: string | undefined = '';

        if (mediaType.AUDIO) media = 'AUDIO';
        if (mediaType.EXTERNAL) media += media.length > 0 ? ',EXTERNAL' : 'EXTERNAL';
        if (mediaType.HTML) media += media.length > 0 ? ',HTML' : 'HTML';
        if (mediaType.VIDEO) media += media.length > 0 ? ',VIDEO' : 'VIDEO';

        if (media.length <= 0) media = undefined;

        let locale: string | undefined = '';

        if (!language.pt && !language.en) locale = undefined;
        else if (language.pt && language.en) locale = undefined;
        else locale = language.pt ? 'pt' : 'en';

        const limit: number = 100;
        const offset: number = index * limit;

        const queryParams = getQueryParams({
            applicationId,
            orgId,
            name: searchName,
            minWorkload: slideRange[0].toString(),
            maxWorkload: slideRange[1] < 90 ? slideRange[1].toString() : undefined,
            status: active.toUpperCase(),
            visibility: visibilityParam,
            orderBy: orderBy === orderByFilterEnum.ALPHABETICAL ? undefined : orderBy,
            mediaType: media,
            locale,
            moduleId: Constants.PLAYLIST_MODULE_ID,
            offset,
            limit,
        });

        return Constants.ITENS_URI + queryParams;
    };

    const { data: itens, loading, total, loadMore, revalidateResult } = useGetPaginated<Item>(getKey, { revalidateOnMount: true });

    const callback = useCallback(() => {
        if (!loading && total && itens.length < total) {
            setShowBottomLoading(true);
            loadMore();
        } else if (!loading) setShowBottomLoading(false);
    }, [itens, searchName]);

    useBottomScrollListener(callback, { offset: 150 });

    useEffect(() => {
        if (!loading) setInitialLoading(false);
        if (itens && itens.length > 0) setInitialData(true);
    }, [loading, itens]);

    useEffect(() => {
        if (refreshItem) {
            revalidateResult();
        }
    }, [refreshItem]);

    const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
        setSearchName(e.target.value);
    };

    const handleNewItem = (): void => {
        history.push('/items/create');
    };

    return (
        <>
            {initialLoading && <Loader />}
            {!initialLoading && (
                <div className="items">
                    <div className="list">
                        <div className="d-flex top-content">
                            <button className="btn btn-primary btn-sm new-item-button" onClick={handleNewItem} type="button">
                                {strings.CREATE_NEW_ITEM}
                            </button>
                            <div className="form-group inner-addon left-addon">
                                <i className="material-icons-outlined align-middle input-icon">search</i>
                                <input
                                    autoComplete="nope"
                                    className="form-control"
                                    onChange={handleSearch}
                                    placeholder={strings.SEARCH}
                                    type="search"
                                    value={searchName}
                                />
                            </div>
                        </div>

                        <Filters
                            entityType={Constants.entityTypes.CONTENT}
                            fakeSlideRange={fakeSlideRange}
                            language={language}
                            mediaType={mediaType}
                            orderBy={orderBy}
                            setFakeSlideRange={setFakeSlideRange}
                            setLanguage={setLanguage}
                            setMediaType={setMediaType}
                            setOrderBy={setOrderBy}
                            setSlideRange={setSlideRange}
                            setVisibility={setVisibility}
                            slideRange={slideRange}
                            status={active}
                            visibility={visibility}
                        />

                        <>
                            <div className="tabs-container">
                                <Tab
                                    active={active === Constants.lessonStatus.PUBLISHED}
                                    icon="outlined_flag"
                                    setActive={() => setActive(Constants.lessonStatus.PUBLISHED)}
                                    title={strings.PUBLISHED}
                                />
                                <Tab
                                    active={active === Constants.lessonStatus.DRAFT}
                                    icon="gesture"
                                    setActive={() => setActive(Constants.lessonStatus.DRAFT)}
                                    title={strings.DRAFT}
                                />
                                <Tab
                                    active={active === Constants.lessonStatus.ARCHIVED}
                                    icon="how_to_vote"
                                    setActive={() => setActive(Constants.lessonStatus.ARCHIVED)}
                                    title={strings.ARCHIVED}
                                />
                            </div>
                            <div className="list-container">
                                {loading && !showBottomLoading ? (
                                    <Loader />
                                ) : (
                                    itens &&
                                    (itens.length > 0 || searchName !== '' || initialData) && (
                                        <div className="list-items">
                                            {itens && itens.length > 0 && itens.map((item: Item) => <ItemCard item={item} key={item.id} />)}
                                        </div>
                                    )
                                )}

                                {!loading && itens && itens.length <= 0 && (
                                    <EmptyList
                                        buttonTitle={strings.CREATE_NEW_ITEM}
                                        handleNewList={handleNewItem}
                                        icon={Constants.icons.BALLOT}
                                        title={strings.EMPTY_ITEMS}
                                    />
                                )}
                            </div>
                            {showBottomLoading && <Loader />}
                        </>
                    </div>
                </div>
            )}
        </>
    );
};

export default Items;
